import {
  AppstoreOutlined,
  AuditOutlined,
  CommentOutlined,
  DashboardOutlined,
  DollarOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  OrderedListOutlined,
  ShopOutlined,
  TeamOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, apiUrl, dateTime, money } from './api';

type View =
  | 'overview'
  | 'applications'
  | 'stores'
  | 'catalog'
  | 'inventory'
  | 'orders'
  | 'shipping'
  | 'afterSales'
  | 'chat'
  | 'refunds'
  | 'commissions'
  | 'users'
  | 'agreements'
  | 'integrations'
  | 'audit';

interface Session {
  id: string;
  displayName: string;
  contexts: Array<{ role: string }>;
  passwordMustChange?: boolean;
}

interface QuickField {
  key: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  options?: Array<{ label: string; value: string }>;
}

function actionModal(
  title: string,
  fields: QuickField[],
  submit: (values: Record<string, string>) => Promise<void>,
) {
  const values = Object.fromEntries(fields.map((field) => [field.key, field.initialValue ?? '']));
  Modal.confirm({
    title,
    width: 520,
    okText: '确认',
    cancelText: '取消',
    content: (
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
        {fields.map((field) => (
          <label key={field.key} style={{ display: 'grid', gap: 6 }}>
            <span>{field.label}</span>
            {field.options ? (
              <Select
                allowClear={!field.required}
                showSearch
                optionFilterProp="label"
                defaultValue={field.initialValue || undefined}
                options={field.options}
                onChange={(value) => (values[field.key] = value ?? '')}
              />
            ) : field.multiline ? (
              <Input.TextArea
                defaultValue={field.initialValue}
                placeholder={field.placeholder}
                onChange={(event) => (values[field.key] = event.target.value.trim())}
              />
            ) : (
              <Input
                defaultValue={field.initialValue}
                placeholder={field.placeholder}
                onChange={(event) => (values[field.key] = event.target.value.trim())}
              />
            )}
          </label>
        ))}
      </Space>
    ),
    onOk: async () => {
      const missing = fields.find((field) => field.required && !values[field.key]);
      if (missing) throw new Error(`请填写${missing.label}`);
      await submit(values);
    },
  });
}

const labels: Record<View, string> = {
  overview: '运营总览',
  applications: '门店认证',
  stores: '门店与归属',
  catalog: '商品与价格',
  inventory: '库存台账',
  orders: '订单履约',
  shipping: '物流管理',
  afterSales: '售后单',
  chat: '聊天队列',
  refunds: '退款账单',
  commissions: '提成管理',
  users: '员工权限',
  agreements: '协议版本',
  integrations: '集成与重试',
  audit: '审计记录',
};

const menuItems: MenuProps['items'] = [
  { key: 'overview', icon: <DashboardOutlined />, label: labels.overview },
  {
    key: 'customers',
    icon: <ShopOutlined />,
    label: '客户',
    children: [
      { key: 'applications', label: labels.applications },
      { key: 'stores', label: labels.stores },
    ],
  },
  { key: 'catalog', icon: <AppstoreOutlined />, label: labels.catalog },
  { key: 'inventory', icon: <AppstoreOutlined />, label: labels.inventory },
  { key: 'orders', icon: <OrderedListOutlined />, label: labels.orders },
  { key: 'shipping', icon: <TruckOutlined />, label: labels.shipping },
  { key: 'afterSales', icon: <MedicineBoxOutlined />, label: labels.afterSales },
  { key: 'chat', icon: <CommentOutlined />, label: labels.chat },
  { key: 'refunds', icon: <DollarOutlined />, label: labels.refunds },
  { key: 'commissions', icon: <DollarOutlined />, label: labels.commissions },
  { key: 'users', icon: <TeamOutlined />, label: labels.users },
  {
    key: 'system',
    icon: <AuditOutlined />,
    label: '系统',
    children: [
      { key: 'agreements', label: labels.agreements },
      { key: 'integrations', label: labels.integrations },
      { key: 'audit', label: labels.audit },
    ],
  },
];

const viewRoles: Record<View, string[]> = {
  overview: ['operations', 'finance', 'support', 'super_admin'],
  applications: ['operations', 'super_admin'],
  stores: ['operations', 'super_admin'],
  catalog: ['operations', 'super_admin'],
  inventory: ['operations', 'super_admin'],
  orders: ['operations', 'finance', 'support', 'super_admin'],
  shipping: ['operations', 'super_admin'],
  afterSales: ['support', 'operations', 'finance', 'super_admin'],
  chat: ['support', 'super_admin'],
  refunds: ['finance', 'super_admin'],
  commissions: ['finance', 'super_admin'],
  users: ['super_admin'],
  agreements: ['operations', 'super_admin'],
  integrations: ['operations', 'super_admin'],
  audit: ['super_admin'],
};

function ChangePassword({ onChanged }: { onChanged: (session: Session) => void }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  return (
    <div className="login-shell">
      <Card className="login-card" bordered={false}>
        <Typography.Title level={2}>首次登录请修改密码</Typography.Title>
        <Typography.Paragraph type="secondary">
          临时密码只能用于这一次登录。新密码至少 12 位，并建议混合大小写、数字和符号。
        </Typography.Paragraph>
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            setLoading(true);
            try {
              await api('/auth/admin/password', {
                method: 'POST',
                json: { currentPassword: values.currentPassword, newPassword: values.newPassword },
              });
              onChanged(await api<Session>('/auth/me'));
              message.success('密码已修改，其他旧会话已撤销');
            } catch (error) {
              message.error(error instanceof Error ? error.message : '修改失败');
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Item name="currentPassword" label="当前临时密码" rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={[{ required: true, min: 12 }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="再次输入新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue('newPassword') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            确认修改
          </Button>
        </Form>
      </Card>
    </div>
  );
}

function useResource(path: string) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api<any[] | { items: any[] }>(path);
      setRows(Array.isArray(result) ? result : result.items);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    void load();
  }, [load]);
  return { rows, loading, load };
}

function InternalNotesButton({
  resourceType,
  resourceId,
}: {
  resourceType: 'store' | 'product' | 'order' | 'after_sales';
  resourceId: string;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [body, setBody] = useState('');
  const load = async () => {
    setRows(await api<any[]>(`/admin/internal-notes/${resourceType}/${resourceId}`));
  };
  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setOpen(true);
          void load();
        }}
      >
        内部备注
      </Button>
      <Modal title="仅员工可见的内部备注" open={open} footer={null} onCancel={() => setOpen(false)}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert type="warning" showIcon message="此内容永不发送到门店聊天或小程序接口" />
          <Input.TextArea
            value={body}
            rows={3}
            onChange={(event) => setBody(event.target.value)}
            placeholder="记录判断依据、协作信息或跟进事项"
          />
          <Button
            type="primary"
            disabled={!body.trim()}
            onClick={async () => {
              await api(`/admin/internal-notes/${resourceType}/${resourceId}`, {
                method: 'POST',
                json: { content: body.trim() },
              });
              setBody('');
              await load();
            }}
          >
            添加备注
          </Button>
          {rows.map((row) => (
            <Card size="small" key={row.id}>
              <p>{row.body}</p>
              <Typography.Text type="secondary">
                {row.authorRoleSnapshot} · {dateTime(row.createdAt)}
              </Typography.Text>
            </Card>
          ))}
        </Space>
      </Modal>
    </>
  );
}

function OrderDetailButton({ orderId }: { orderId: string }) {
  const [detail, setDetail] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="small"
        onClick={async () => {
          setOpen(true);
          setDetail(await api<any>(`/admin/orders/${orderId}`));
        }}
      >
        详情
      </Button>
      <Modal
        title={detail ? `订单 ${detail.orderNumber}` : '订单详情'}
        open={open}
        width={900}
        footer={null}
        onCancel={() => setOpen(false)}
      >
        {!detail ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small" title="金额与状态">
              <Space wrap>
                {statusTag(detail.paymentStatus)}
                {statusTag(detail.fulfillmentStatus)}
                {statusTag(detail.orderStatus)}
                <Typography.Text>商品 {money(detail.goodsAmountCents)}</Typography.Text>
                <Typography.Text>运费 {money(detail.shippingAmountCents)}</Typography.Text>
                <Typography.Text strong>实付 {money(detail.totalAmountCents)}</Typography.Text>
              </Space>
            </Card>
            <Card size="small" title="收货与买家留言">
              <p>
                {detail.shippingAddressSnapshot?.recipientName} ·{' '}
                {detail.shippingAddressSnapshot?.recipientPhone}
              </p>
              <p>
                {detail.shippingAddressSnapshot?.province}
                {detail.shippingAddressSnapshot?.city}
                {detail.shippingAddressSnapshot?.district}
                {detail.shippingAddressSnapshot?.detail}
              </p>
              <Typography.Text type="secondary">{detail.buyerNote || '无买家留言'}</Typography.Text>
            </Card>
            <Card size="small" title="订单行快照">
              {(detail.items ?? []).map((item: any) => (
                <p key={item.id}>
                  {item.productNameSnapshot} · {item.skuCodeSnapshot} ·{' '}
                  {JSON.stringify(item.specificationsSnapshot)} · {item.quantity} 件 ·{' '}
                  {money(item.actualUnitPriceCents)} / 件 · 已发 {item.shippedQuantity} · 已签收{' '}
                  {item.deliveredQuantity} · 已取消 {item.cancelledQuantity} · 已退款{' '}
                  {item.refundedQuantity}
                </p>
              ))}
            </Card>
            <Card size="small" title="包裹与物流时间线">
              {!(detail.shipments ?? []).length ? (
                <Typography.Text type="secondary">尚未发货</Typography.Text>
              ) : (
                (detail.shipments ?? []).map((shipment: any) => (
                  <div key={shipment.id}>
                    <p>
                      <strong>{shipment.shipmentNumber}</strong> · {shipment.carrierName}{' '}
                      {shipment.trackingNumber} · {shipment.status}
                    </p>
                    {(shipment.events ?? []).map((event: any) => (
                      <p key={event.id}>
                        <Typography.Text type="secondary">
                          {dateTime(event.occurredAt)} · {event.description}
                          {event.correctionReason ? ` · ${event.correctionReason}` : ''}
                        </Typography.Text>
                      </p>
                    ))}
                  </div>
                ))
              )}
            </Card>
          </Space>
        )}
      </Modal>
    </>
  );
}

function Login({ onLogin }: { onLogin: (session: Session) => void }) {
  const [loading, setLoading] = useState(false);
  const submit = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      await api('/auth/admin/login', { method: 'POST', json: values });
      onLogin(await api<Session>('/auth/me'));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '登录失败');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="login-shell">
      <div className="login-brand">
        <span className="brand-kicker">KAMEI SUPPLY CHAIN</span>
        <h1>把每一次供货，变成可追踪的承诺。</h1>
        <p>门店认证、商品定价、订单履约、售后与提成，在一个清晰的工作台里完成。</p>
      </div>
      <Card className="login-card" bordered={false}>
        <Typography.Title level={2}>管理中心</Typography.Title>
        <Typography.Paragraph type="secondary">仅限卡美内部授权账号</Typography.Paragraph>
        <Form layout="vertical" onFinish={submit} size="large">
          <Form.Item name="username" label="账号" rules={[{ required: true }]}>
            <Input autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>
            安全登录
          </Button>
        </Form>
      </Card>
    </div>
  );
}

function Overview() {
  const [data, setData] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    api<Record<string, number>>('/admin/overview')
      .then(setData)
      .catch((e) => message.error(e.message));
  }, []);
  if (!data) return <Spin />;
  const cards = [
    ['待审门店', data.pendingApplications],
    ['商品总数', data.products],
    ['进行中订单', data.openOrders],
    ['待处理售后', data.openAfterSales],
    ['待复核退款', data.pendingRefunds],
    ['未认领会话', data.unclaimedConversations],
    ['库存预警 SKU', data.lowInventorySkus],
    ['支付/退款异常', data.paymentAnomalies],
    ['事件队列积压', data.outboxBacklog],
  ];
  return (
    <>
      <div className="hero-card">
        <span>今日工作台</span>
        <h2>关键事务，按风险优先处理。</h2>
        <p>所有金额、归属和提成均以订单快照为准；历史不会被今天的改价重写。</p>
      </div>
      <Row gutter={[16, 16]}>
        {cards.map(([title, value]) => (
          <Col xs={24} sm={12} lg={8} key={String(title)}>
            <Card bordered={false}>
              <Statistic title={title} value={value} valueStyle={{ color: '#0B2A4A' }} />
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );
}

function Applications() {
  const resource = useResource('/admin/stores/applications');
  const review = async (id: string, decision: 'verified' | 'rejected') => {
    if (decision === 'rejected') {
      actionModal(
        '驳回认证申请',
        [{ key: 'reason', label: '驳回原因', required: true, multiline: true }],
        async ({ reason }) => {
          await api(`/admin/stores/applications/${id}/review`, {
            method: 'POST',
            json: { decision, reason, assignmentType: 'headquarters_direct' },
          });
          message.success('已驳回');
          await resource.load();
        },
      );
      return;
    }
    await api(`/admin/stores/applications/${id}/review`, {
      method: 'POST',
      json: {
        decision,
        assignmentType: 'headquarters_direct',
      },
    });
    message.success('已认证并设为总部直营');
    await resource.load();
  };
  const columns: TableColumnsType<any> = [
    { title: '门店', dataIndex: 'storeName' },
    { title: '信用代码', dataIndex: 'unifiedSocialCreditCode' },
    { title: '联系人', render: (_, r) => `${r.contactName} · ${r.contactPhone}` },
    { title: '地址', dataIndex: 'businessAddress' },
    {
      title: '资料',
      render: (_, row) => (
        <Space>
          <a href={apiUrl(`/files/${row.businessLicenseFileKey}`)} target="_blank" rel="noreferrer">
            营业执照
          </a>
          <a href={apiUrl(`/files/${row.storefrontPhotoFileKey}`)} target="_blank" rel="noreferrer">
            门头
          </a>
        </Space>
      ),
    },
    { title: '状态', dataIndex: 'status', render: statusTag },
    { title: '申请时间', dataIndex: 'createdAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) =>
        row.status === 'pending' && (
          <Space>
            <Button type="primary" onClick={() => void review(row.id, 'verified')}>
              认证
            </Button>
            <Button danger onClick={() => void review(row.id, 'rejected')}>
              驳回
            </Button>
          </Space>
        ),
    },
  ];
  return (
    <Table rowKey="id" columns={columns} dataSource={resource.rows} loading={resource.loading} />
  );
}

function Stores() {
  const resource = useResource('/admin/stores');
  const salespeople = useResource('/admin/staff-options?role=salesperson');
  const priceTiers = useResource('/admin/catalog/price-tiers');
  const changeStatus = async (row: any) => {
    const status = row.status === 'suspended' ? 'verified' : 'suspended';
    actionModal(
      status === 'suspended' ? '停用门店' : '恢复门店',
      [
        {
          key: 'reason',
          label: '操作原因',
          required: true,
          multiline: true,
          initialValue: status === 'verified' ? '恢复门店交易权限' : '',
        },
      ],
      async ({ reason }) => {
        await api(`/admin/stores/${row.id}/status`, {
          method: 'PATCH',
          json: { status, reason },
        });
        message.success(status === 'suspended' ? '门店已停用' : '门店已恢复');
        await resource.load();
      },
    );
  };
  const updateCommercial = async (row: any) => {
    actionModal(
      '调整门店交易条件',
      [
        {
          key: 'minimum',
          label: '最低订单金额（分）',
          required: true,
          initialValue: String(row.minimumOrderAmountCents ?? 0),
        },
        {
          key: 'priceTierId',
          label: '价格等级（留空使用标准价）',
          initialValue: row.priceTierId ?? '',
          options: priceTiers.rows.map((tier) => ({ label: tier.name, value: tier.id })),
        },
      ],
      async ({ minimum, priceTierId }) => {
        if (!Number.isInteger(Number(minimum)) || Number(minimum) < 0)
          throw new Error('最低订单金额必须为非负整数');
        await api(`/admin/stores/${row.id}/commercial`, {
          method: 'PATCH',
          json: { minimumOrderAmountCents: Number(minimum), priceTierId: priceTierId || null },
        });
        message.success('交易条件已更新');
        await resource.load();
      },
    );
  };
  const reassign = async (row: any) => {
    actionModal(
      '调整客户归属',
      [
        {
          key: 'salespersonUserId',
          label: '业务员（留空改为总部直营）',
          options: salespeople.rows.map((person) => ({
            label: person.displayName,
            value: person.id,
          })),
        },
        {
          key: 'reason',
          label: '改绑原因',
          required: true,
          initialValue: '客户归属调整',
          multiline: true,
        },
      ],
      async ({ salespersonUserId, reason }) => {
        await api(`/admin/stores/${row.id}/assignment`, {
          method: 'POST',
          json: {
            assignmentType: salespersonUserId ? 'salesperson' : 'headquarters_direct',
            salespersonUserId: salespersonUserId || undefined,
            reason,
          },
        });
        message.success('客户归属已更新，仅影响新订单');
        await resource.load();
      },
    );
  };
  const columns: TableColumnsType<any> = [
    { title: '门店', dataIndex: 'name' },
    { title: '信用代码', dataIndex: 'unifiedSocialCreditCode' },
    { title: '状态', dataIndex: 'status', render: statusTag },
    {
      title: '客户归属',
      render: (_, r) =>
        r.assignment?.type === 'salesperson'
          ? salespeople.rows.find((person) => person.id === r.assignment.salespersonUserId)
              ?.displayName || '业务员（账号已停用）'
          : '总部直营',
    },
    { title: '最低订单金额', dataIndex: 'minimumOrderAmountCents', render: money },
    { title: '更新时间', dataIndex: 'updatedAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) => (
        <Space wrap>
          <Button size="small" onClick={() => void updateCommercial(row)}>
            价格/起订
          </Button>
          <Button size="small" onClick={() => void reassign(row)}>
            改绑
          </Button>
          <InternalNotesButton resourceType="store" resourceId={row.id} />
          <Button
            danger={row.status !== 'suspended'}
            size="small"
            onClick={() => void changeStatus(row)}
          >
            {row.status === 'suspended' ? '恢复' : '停用'}
          </Button>
        </Space>
      ),
    },
  ];
  return (
    <Table rowKey="id" columns={columns} dataSource={resource.rows} loading={resource.loading} />
  );
}

function Catalog() {
  const resource = useResource('/admin/catalog/products');
  const stores = useResource('/admin/stores');
  const priceTiers = useResource('/admin/catalog/price-tiers');
  const brands = useResource('/admin/catalog/brands');
  const categories = useResource('/admin/catalog/categories');
  const [uploading, setUploading] = useState(false);
  const [importPreview, setImportPreview] = useState<any | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [revisionPreview, setRevisionPreview] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [revisionForm] = Form.useForm();
  const createProduct = async () => {
    await api('/admin/catalog/products', {
      method: 'POST',
      json: {
        ...(await form.validateFields()),
        isPublished: false,
        isPubliclyVisible: true,
      },
    });
    setOpen(false);
    form.resetFields();
    message.success('商品草稿已创建，请通过 Excel 导入 SKU');
    await resource.load();
  };
  const unpublish = async (row: any) => {
    await api(`/admin/catalog/products/${row.id}`, {
      method: 'PATCH',
      json: { isPublished: false },
    });
    message.success('商品已下架；历史订单快照不受影响');
    await resource.load();
  };
  const openEditor = (row: any) => {
    setEditing(row);
    revisionForm.setFieldsValue({
      name: row.name,
      brand: row.brand,
      category: row.category,
      summary: row.summary,
      description: row.description ?? '',
      coverImageKey: row.coverImageKey ?? '',
      tagsText: (row.publishedRevision?.tags ?? []).join('，'),
      skus: (row.skus ?? []).map((sku: any) => ({
        ...sku,
        specificationsText: Object.entries(sku.specifications ?? {})
          .map(([key, value]) => `${key}=${value}`)
          .join('；'),
        priceYuan: sku.standardPriceCents / 100,
        commissionPercent: sku.commissionRateBasisPoints / 100,
      })),
    });
  };
  const createRevisionPreview = async () => {
    if (!editing) return;
    const values = await revisionForm.validateFields();
    const parseSpecifications = (text: string) =>
      Object.fromEntries(
        text
          .split(/[；;]/)
          .map((part) => part.trim())
          .filter(Boolean)
          .map((part) => {
            const separator = part.indexOf('=');
            if (separator < 1) throw new Error(`规格“${part}”应使用 名称=值`);
            return [part.slice(0, separator).trim(), part.slice(separator + 1).trim()];
          }),
      );
    const snapshot = {
      name: values.name,
      brand: values.brand,
      category: values.category,
      summary: values.summary,
      description: values.description || null,
      coverImageKey: values.coverImageKey || null,
      isPubliclyVisible: true,
      skus: values.skus.map((sku: any) => ({
        ...(sku.id ? { id: sku.id } : {}),
        code: sku.code,
        specifications: parseSpecifications(sku.specificationsText),
        salesUnit: sku.salesUnit,
        unitsPerSalesUnit: Number(sku.unitsPerSalesUnit),
        minimumQuantity: Number(sku.minimumQuantity),
        quantityStep: Number(sku.quantityStep),
        billingWeightGrams: Number(sku.billingWeightGrams),
        standardPriceCents: Math.round(Number(sku.priceYuan) * 100),
        commissionRateBasisPoints: Math.round(Number(sku.commissionPercent) * 100),
        availability: sku.availability,
        expectedShipDate: sku.expectedShipDate || null,
        erpSkuCode: sku.erpSkuCode || null,
        isActive: sku.isActive !== false,
      })),
    };
    const revision = await api<any>(`/admin/catalog/products/${editing.id}/revisions`, {
      method: 'POST',
      json: {
        snapshot,
        contentBlocks: values.description ? [{ type: 'text', text: values.description }] : [],
        tags: String(values.tagsText ?? '')
          .split(/[，,]/)
          .map((tag) => tag.trim())
          .filter(Boolean),
      },
    });
    setEditing(null);
    setRevisionPreview({ ...revision, snapshot, productId: editing.id });
  };
  const columns: TableColumnsType<any> = [
    { title: '商品', dataIndex: 'name' },
    { title: '品牌', dataIndex: 'brand' },
    { title: '分类', dataIndex: 'category' },
    {
      title: '上架',
      dataIndex: 'isPublished',
      render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? '已上架' : '草稿'}</Tag>,
    },
    { title: 'SKU', render: (_, r) => r.skus?.length ?? 0 },
    {
      title: '供货价区间',
      render: (_, r) => {
        const prices = (r.skus ?? []).map((s: any) => s.standardPriceCents);
        return prices.length
          ? `${money(Math.min(...prices))} – ${money(Math.max(...prices))}`
          : '—';
      },
    },
    {
      title: '操作',
      render: (_, row) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openEditor(row)}>
            编辑并预览
          </Button>
          <InternalNotesButton resourceType="product" resourceId={row.id} />
          {row.isPublished && (
            <Button danger size="small" onClick={() => void unpublish(row)}>
              下架
            </Button>
          )}
        </Space>
      ),
    },
  ];
  return (
    <>
      <Alert
        className="page-alert"
        type="info"
        showIcon
        message="游客接口不会返回价格、库存或购买能力；价格只从鉴权后的交易目录产生。"
      />
      <Space wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          新建商品
        </Button>
        <Button
          onClick={() =>
            actionModal(
              '新建品牌',
              [{ key: 'name', label: '品牌名称', required: true }],
              async ({ name }) => {
                await api('/admin/catalog/brands', { method: 'POST', json: { name } });
                message.success('品牌已创建');
                await brands.load();
              },
            )
          }
        >
          新建品牌
        </Button>
        <Button
          onClick={() =>
            actionModal(
              '新建一级分类',
              [
                { key: 'code', label: '分类编码', required: true },
                { key: 'name', label: '分类名称', required: true },
              ],
              async (values) => {
                await api('/admin/catalog/categories', { method: 'POST', json: values });
                message.success('一级分类已创建');
                await categories.load();
              },
            )
          }
        >
          新建一级分类
        </Button>
        <Button
          onClick={() =>
            actionModal(
              '新建二级分类',
              [
                {
                  key: 'parentId',
                  label: '所属一级分类',
                  required: true,
                  options: categories.rows
                    .filter((item) => item.level === 1)
                    .map((item) => ({ label: item.name, value: item.id })),
                },
                { key: 'code', label: '分类编码', required: true },
                { key: 'name', label: '分类名称', required: true },
              ],
              async (values) => {
                await api('/admin/catalog/categories', { method: 'POST', json: values });
                message.success('二级分类已创建');
                await categories.load();
              },
            )
          }
        >
          新建二级分类
        </Button>
        <Upload
          showUploadList={false}
          accept=".xlsx"
          customRequest={async ({ file, onSuccess, onError }) => {
            setUploading(true);
            const uploadForm = new FormData();
            uploadForm.append('file', file as File);
            try {
              const preview = await api<any>('/admin/catalog/import/preflight', {
                method: 'POST',
                body: uploadForm,
              });
              setImportPreview(preview);
              message.success(
                preview.errors.length ? '预检完成，请修正错误' : '预检通过，请确认差异',
              );
              onSuccess?.({});
            } catch (error) {
              onError?.(error as Error);
              message.error((error as Error).message);
            } finally {
              setUploading(false);
            }
          }}
        >
          <Button loading={uploading}>导入 Excel</Button>
        </Upload>
        <Button href={apiUrl('/admin/catalog/import-template')}>下载模板</Button>
        <Button href={apiUrl('/admin/catalog/export')}>导出商品</Button>
        <Button
          onClick={() => {
            actionModal(
              '新建价格等级',
              [{ key: 'name', label: '等级名称', required: true }],
              async ({ name }) => {
                await api('/admin/catalog/price-tiers', { method: 'POST', json: { name } });
                message.success('价格等级已创建');
              },
            );
          }}
        >
          新建价格等级
        </Button>
        <Button
          onClick={() => {
            actionModal(
              '设置分类售后期限',
              [
                { key: 'category', label: '商品分类', required: true },
                { key: 'days', label: '签收后可售后天数', required: true, initialValue: '7' },
              ],
              async ({ category, days }) => {
                if (!Number.isInteger(Number(days)) || Number(days) < 0)
                  throw new Error('天数必须为非负整数');
                await api('/admin/catalog/category-policies', {
                  method: 'POST',
                  json: { category, afterSalesWindowDays: Number(days) },
                });
                message.success('分类售后期限已保存');
              },
            );
          }}
        >
          售后期限
        </Button>
        <Button
          onClick={() => {
            actionModal(
              '新建专项价或等级价',
              [
                {
                  key: 'skuId',
                  label: 'SKU',
                  required: true,
                  options: resource.rows.flatMap((product) =>
                    (product.skus ?? []).map((sku: any) => ({
                      label: `${product.name} · ${sku.code}`,
                      value: sku.id,
                    })),
                  ),
                },
                { key: 'unitPriceCents', label: '供货单价（分）', required: true },
                {
                  key: 'storeId',
                  label: '门店（专项价时选择）',
                  options: stores.rows.map((store) => ({ label: store.name, value: store.id })),
                },
                {
                  key: 'priceTierId',
                  label: '价格等级（等级价时选择）',
                  options: priceTiers.rows.map((tier) => ({ label: tier.name, value: tier.id })),
                },
              ],
              async ({ skuId, unitPriceCents, storeId, priceTierId }) => {
                if ((!storeId && !priceTierId) || (storeId && priceTierId))
                  throw new Error('门店编号与价格等级编号必须且只能填写一个');
                await api('/admin/catalog/price-rules', {
                  method: 'POST',
                  json: {
                    skuId,
                    unitPriceCents: Number(unitPriceCents),
                    storeId: storeId || undefined,
                    priceTierId: priceTierId || undefined,
                  },
                });
                message.success('分级价格已保存');
              },
            );
          }}
        >
          新建专项/等级价
        </Button>
        <Button
          onClick={() => {
            actionModal(
              '新建区域运费模板',
              [
                { key: 'name', label: '模板名称', required: true },
                { key: 'baseFeeCents', label: '首费（分）', required: true, initialValue: '0' },
                {
                  key: 'firstWeightGrams',
                  label: '首重（克）',
                  required: true,
                  initialValue: '1000',
                },
                {
                  key: 'incrementWeightGrams',
                  label: '续重单位（克）',
                  required: true,
                  initialValue: '1000',
                },
                {
                  key: 'incrementFeeCents',
                  label: '每个续重单位费用（分）',
                  required: true,
                  initialValue: '0',
                },
                { key: 'freeThresholdCents', label: '包邮门槛（分，留空表示不包邮）' },
                { key: 'provinces', label: '适用省份（英文逗号分隔，留空作为默认）' },
              ],
              async (values) => {
                await api('/admin/catalog/shipping-templates', {
                  method: 'POST',
                  json: {
                    name: values.name,
                    baseFeeCents: Number(values.baseFeeCents),
                    firstWeightGrams: Number(values.firstWeightGrams),
                    incrementWeightGrams: Number(values.incrementWeightGrams),
                    incrementFeeCents: Number(values.incrementFeeCents),
                    freeThresholdCents: values.freeThresholdCents
                      ? Number(values.freeThresholdCents)
                      : undefined,
                    provinces: values.provinces
                      ? values.provinces.split(',').map((item) => item.trim())
                      : undefined,
                    isDefault: !values.provinces,
                    isActive: true,
                  },
                });
                message.success('运费模板已保存');
              },
            );
          }}
        >
          新建运费模板
        </Button>
      </Space>
      <Table
        className="data-table"
        rowKey="id"
        columns={columns}
        dataSource={resource.rows}
        loading={resource.loading}
      />
      <Modal
        title="新建商品草稿"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void createProduct()}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="商品名称" rules={[{ required: true, min: 2 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="brand" label="品牌" initialValue="">
            <Select
              showSearch
              optionFilterProp="label"
              options={brands.rows.map((item) => ({ label: item.name, value: item.name }))}
            />
          </Form.Item>
          <Form.Item name="category" label="分类" initialValue="">
            <Select
              showSearch
              optionFilterProp="label"
              options={categories.rows
                .filter((item) => item.level === 2)
                .map((item) => ({ label: item.name, value: item.name }))}
            />
          </Form.Item>
          <Form.Item name="summary" label="简介" initialValue="">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="商品草稿与 SKU 矩阵"
        open={!!editing}
        width={960}
        okText="生成公开/门店双视图预览"
        onCancel={() => setEditing(null)}
        onOk={() => void createRevisionPreview()}
      >
        <Form form={revisionForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="商品名称" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="brand" label="品牌" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={brands.rows.map((item) => ({ label: item.name, value: item.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="category" label="二级分类" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={categories.rows
                    .filter((item) => item.level === 2)
                    .map((item) => ({ label: item.name, value: item.name }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="summary" label="列表简介" rules={[{ required: true, max: 255 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="商品详情正文">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="tagsText" label="标签（逗号分隔）">
            <Input placeholder="热销，新品" />
          </Form.Item>
          <Form.Item label="商品主图">
            <Space>
              <Form.Item name="coverImageKey" noStyle>
                <Input readOnly placeholder="尚未上传" style={{ width: 320 }} />
              </Form.Item>
              <Upload
                showUploadList={false}
                accept="image/jpeg,image/png,image/webp"
                customRequest={async ({ file, onSuccess, onError }) => {
                  const data = new FormData();
                  data.append('file', file as File);
                  try {
                    const uploaded = await api<{ id: string }>(
                      '/files?visibility=public&purpose=catalog',
                      { method: 'POST', body: data },
                    );
                    revisionForm.setFieldValue('coverImageKey', uploaded.id);
                    onSuccess?.({});
                  } catch (error) {
                    onError?.(error as Error);
                  }
                }}
              >
                <Button>上传主图</Button>
              </Upload>
            </Space>
          </Form.Item>
          <Typography.Title level={5}>有效 SKU 组合</Typography.Title>
          <Form.List
            name="skus"
            rules={[
              {
                validator: async (_, rows) =>
                  rows?.length ? Promise.resolve() : Promise.reject(new Error('至少需要一个 SKU')),
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={`SKU ${index + 1}`}
                    extra={
                      <Button danger size="small" onClick={() => remove(field.name)}>
                        移除
                      </Button>
                    }
                  >
                    <Form.Item name={[field.name, 'id']} hidden>
                      <Input />
                    </Form.Item>
                    <Row gutter={12}>
                      <Col span={6}>
                        <Form.Item
                          name={[field.name, 'code']}
                          label="SKU 编码"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item
                          name={[field.name, 'specificationsText']}
                          label="规格（名称=值；名称=值）"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'salesUnit']}
                          label="销售单位"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'unitsPerSalesUnit']}
                          label="单位换算"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'minimumQuantity']}
                          label="起订量"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'quantityStep']}
                          label="增购步长"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'billingWeightGrams']}
                          label="计费重量(克)"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'priceYuan']}
                          label="标准供货价(元)"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'commissionPercent']}
                          label="提成率(%)"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[field.name, 'availability']}
                          label="供货状态"
                          rules={[{ required: true }]}
                        >
                          <Select
                            options={['in_stock', 'low_stock', 'preorder', 'unavailable'].map(
                              (value) => ({ value, label: value }),
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name={[field.name, 'expectedShipDate']} label="预售预计发货日">
                          <Input placeholder="YYYY-MM-DD" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name={[field.name, 'erpSkuCode']} label="ERP 映射">
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  block
                  onClick={() =>
                    add({
                      unitsPerSalesUnit: 1,
                      minimumQuantity: 1,
                      quantityStep: 1,
                      billingWeightGrams: 0,
                      priceYuan: 0,
                      commissionPercent: 0,
                      availability: 'in_stock',
                      isActive: true,
                    })
                  }
                >
                  添加 SKU 组合
                </Button>
                <Form.ErrorList errors={errors} />
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>
      <Modal
        title={`版本 ${revisionPreview?.version ?? ''} 双视图预览`}
        open={!!revisionPreview}
        width={820}
        okText="确认发布此版本"
        cancelText="保留草稿"
        onCancel={() => setRevisionPreview(null)}
        onOk={async () => {
          await api(
            `/admin/catalog/products/${revisionPreview.productId}/revisions/${revisionPreview.id}/publish`,
            { method: 'POST' },
          );
          message.success('商品版本已发布，只影响后续报价与订单');
          setRevisionPreview(null);
          await resource.load();
        }}
      >
        {revisionPreview && (
          <Row gutter={16}>
            <Col span={12}>
              <Card title="游客公开视图">
                <Typography.Title level={4}>{revisionPreview.snapshot.name}</Typography.Title>
                <p>{revisionPreview.snapshot.summary}</p>
                {revisionPreview.snapshot.skus.map((sku: any) => (
                  <Tag key={sku.code}>{Object.values(sku.specifications).join(' / ')}</Tag>
                ))}
                <Alert type="info" showIcon message="不返回价格、库存或购买能力" />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="认证门店视图">
                <Typography.Title level={4}>{revisionPreview.snapshot.name}</Typography.Title>
                {revisionPreview.snapshot.skus.map((sku: any) => (
                  <p key={sku.code}>
                    {sku.code} · {money(sku.standardPriceCents)} · 起订 {sku.minimumQuantity}{' '}
                    {sku.salesUnit}
                    {sku.availability === 'preorder' ? ` · 预售至 ${sku.expectedShipDate}` : ''}
                  </p>
                ))}
              </Card>
            </Col>
          </Row>
        )}
      </Modal>
      <Modal
        title="Excel 导入预检"
        open={!!importPreview}
        okText="确认写入商品草稿"
        okButtonProps={{ disabled: !!importPreview?.errors?.length }}
        onCancel={() => setImportPreview(null)}
        onOk={async () => {
          if (!importPreview) return;
          await api(`/admin/catalog/import/${importPreview.batchId}/commit`, { method: 'POST' });
          message.success('已原子写入草稿，线上商品未自动发布');
          setImportPreview(null);
          await resource.load();
        }}
      >
        {importPreview && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type={importPreview.errors.length ? 'error' : 'success'}
              showIcon
              message={importPreview.errors.length ? '预检未通过' : '预检通过'}
              description={`共 ${importPreview.diff.rowCount} 行，新增 ${importPreview.diff.newProducts} 个商品草稿、${importPreview.diff.newSkus} 个 SKU`}
            />
            {importPreview.errors.map((error: any) => (
              <Alert
                key={`${error.row}-${error.message}`}
                type="error"
                message={error.row ? `第 ${error.row} 行` : '整表错误'}
                description={error.message}
              />
            ))}
          </Space>
        )}
      </Modal>
    </>
  );
}

function Inventory() {
  const resource = useResource('/admin/inventory');
  const columns: TableColumnsType<any> = [
    { title: 'SKU 编码', dataIndex: 'skuCode' },
    { title: '账面库存', dataIndex: 'onHandQuantity' },
    { title: '待支付预占', dataIndex: 'reservedQuantity' },
    { title: '可售库存', dataIndex: 'availableQuantity' },
    { title: '累计已售', dataIndex: 'soldQuantity' },
    { title: '版本', dataIndex: 'version' },
    { title: '更新时间', dataIndex: 'updatedAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) => (
        <Button
          size="small"
          onClick={() =>
            actionModal(
              '库存调整',
              [
                {
                  key: 'quantityDelta',
                  label: '调整数量（增加填正数，盘亏填负数）',
                  required: true,
                },
                { key: 'reason', label: '调整原因', required: true, multiline: true },
              ],
              async ({ quantityDelta, reason }) => {
                if (!Number.isInteger(Number(quantityDelta)) || Number(quantityDelta) === 0) {
                  throw new Error('调整数量必须为非零整数');
                }
                await api('/admin/inventory/adjustments', {
                  method: 'POST',
                  json: { skuCode: row.skuCode, quantityDelta: Number(quantityDelta), reason },
                });
                message.success('库存调整已记入流水');
                await resource.load();
              },
            )
          }
        >
          调整库存
        </Button>
      ),
    },
  ];
  return (
    <>
      <Alert
        className="page-alert"
        type="warning"
        showIcon
        message="库存调整会写入追加式流水；调整后可售库存不得低于待支付预占。"
      />
      <Button
        type="primary"
        onClick={() =>
          actionModal(
            '为新 SKU 建立库存',
            [
              { key: 'skuCode', label: 'SKU 编码', required: true },
              { key: 'quantityDelta', label: '初始库存数量', required: true },
              { key: 'reason', label: '入库依据', required: true, multiline: true },
            ],
            async ({ skuCode, quantityDelta, reason }) => {
              if (!Number.isInteger(Number(quantityDelta)) || Number(quantityDelta) <= 0) {
                throw new Error('初始库存必须为正整数');
              }
              await api('/admin/inventory/adjustments', {
                method: 'POST',
                json: { skuCode, quantityDelta: Number(quantityDelta), reason },
              });
              message.success('库存已建立');
              await resource.load();
            },
          )
        }
      >
        新 SKU 入库
      </Button>
      <Table
        className="data-table"
        rowKey="id"
        columns={columns}
        dataSource={resource.rows}
        loading={resource.loading}
      />
    </>
  );
}

function Orders({ shipping = false }: { shipping?: boolean }) {
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [fulfillmentStatus, setFulfillmentStatus] = useState('');
  const query = new URLSearchParams({ pageSize: '100' });
  if (search) query.set('search', search);
  if (paymentStatus) query.set('paymentStatus', paymentStatus);
  if (fulfillmentStatus) query.set('fulfillmentStatus', fulfillmentStatus);
  const resource = useResource(`/admin/orders?${query.toString()}`);
  const rows = shipping
    ? resource.rows.filter((row) =>
        ['ready', 'partially_shipped', 'shipped'].includes(row.fulfillmentStatus),
      )
    : resource.rows;
  const columns: TableColumnsType<any> = [
    { title: '订单号', dataIndex: 'orderNumber' },
    { title: '门店', dataIndex: 'storeName' },
    { title: '支付', dataIndex: 'paymentStatus', render: statusTag },
    { title: '履约', dataIndex: 'fulfillmentStatus', render: statusTag },
    { title: '订单', dataIndex: 'orderStatus', render: statusTag },
    { title: '金额', dataIndex: 'totalAmountCents', render: money },
    { title: '下单时间', dataIndex: 'createdAt', render: dateTime },
    {
      title: '详情',
      render: (_, row) => (
        <Space>
          <OrderDetailButton orderId={row.id} />
          <InternalNotesButton resourceType="order" resourceId={row.id} />
        </Space>
      ),
    },
    ...(shipping
      ? [
          {
            title: '操作',
            render: (_: unknown, row: any) => (
              <Space wrap>
                <Button
                  size="small"
                  type="primary"
                  onClick={async () => {
                    const detail = await api<any>(`/admin/orders/${row.id}`);
                    const items = (detail.items ?? [])
                      .map((item: any) => ({
                        orderItemId: item.id,
                        quantity: item.quantity - item.shippedQuantity - item.cancelledQuantity,
                      }))
                      .filter((item: any) => item.quantity > 0);
                    if (!items.length) {
                      message.info('没有可出库数量');
                      return;
                    }
                    actionModal(
                      '登记包裹发货',
                      [
                        { key: 'carrierName', label: '物流公司', required: true },
                        { key: 'trackingNumber', label: '物流单号', required: true },
                        ...items.map((item: any) => {
                          const source = detail.items.find(
                            (candidate: any) => candidate.id === item.orderItemId,
                          );
                          return {
                            key: `quantity:${item.orderItemId}`,
                            label: `${source.productNameSnapshot} / ${source.skuCodeSnapshot}（最多 ${item.quantity}）`,
                            required: true,
                            initialValue: String(item.quantity),
                          };
                        }),
                      ],
                      async (values) => {
                        const selectedItems = items
                          .map((item: any) => ({
                            orderItemId: item.orderItemId,
                            quantity: Number(values[`quantity:${item.orderItemId}`]),
                          }))
                          .filter(
                            (item: any) => Number.isInteger(item.quantity) && item.quantity > 0,
                          );
                        if (!selectedItems.length) throw new Error('至少填写一个大于零的发货数量');
                        for (const selected of selectedItems) {
                          const maximum =
                            items.find((item: any) => item.orderItemId === selected.orderItemId)
                              ?.quantity ?? 0;
                          if (selected.quantity > maximum)
                            throw new Error('发货数量不能超过可发数量');
                        }
                        await api(`/admin/shipments/orders/${row.id}`, {
                          method: 'POST',
                          json: {
                            carrierName: values.carrierName,
                            trackingNumber: values.trackingNumber,
                            shippedAt: new Date().toISOString(),
                            items: selectedItems,
                          },
                        });
                        message.success('包裹已登记并通知门店');
                        await resource.load();
                      },
                    );
                  }}
                >
                  登记剩余发货
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    const detail = await api<any>(`/admin/orders/${row.id}`);
                    const options = (detail.shipments ?? [])
                      .filter((shipment: any) => shipment.status !== 'delivered')
                      .map((shipment: any) => ({
                        label: `${shipment.shipmentNumber} · ${shipment.carrierName} ${shipment.trackingNumber}`,
                        value: shipment.shipmentNumber,
                      }));
                    if (!options.length) {
                      message.info('没有待确认签收的包裹');
                      return;
                    }
                    actionModal(
                      '人工纠正为已签收',
                      [
                        { key: 'shipmentId', label: '包裹', required: true, options },
                        {
                          key: 'reason',
                          label: '签收依据与纠正原因',
                          required: true,
                          multiline: true,
                        },
                      ],
                      async ({ shipmentId, reason }) => {
                        await api(`/admin/shipments/${shipmentId}/delivered`, {
                          method: 'PATCH',
                          json: { deliveredAt: new Date().toISOString(), reason },
                        });
                        message.success('包裹已确认签收');
                        await resource.load();
                      },
                    );
                  }}
                >
                  确认签收
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <Space wrap>
        <Input.Search
          allowClear
          placeholder="订单号、门店名或手机号尾号"
          onSearch={setSearch}
          style={{ width: 300 }}
        />
        <Select
          allowClear
          placeholder="支付状态"
          style={{ width: 150 }}
          onChange={(value) => setPaymentStatus(value ?? '')}
          options={['pending', 'paid', 'partially_refunded', 'refunded', 'closed'].map((value) => ({
            label: value,
            value,
          }))}
        />
        <Select
          allowClear
          placeholder="履约状态"
          style={{ width: 170 }}
          onChange={(value) => setFulfillmentStatus(value ?? '')}
          options={['waiting_stock', 'ready', 'partially_shipped', 'shipped', 'delivered'].map(
            (value) => ({ label: value, value }),
          )}
        />
      </Space>
      <Table
        className="data-table"
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={resource.loading}
      />
    </>
  );
}

function AfterSales({ roles }: { roles: Set<string> }) {
  const resource = useResource('/admin/after-sales');
  const canSupport = roles.has('support') || roles.has('super_admin');
  const canOperate = roles.has('operations') || roles.has('super_admin');
  const canInspect = canSupport || canOperate;
  const canFinance = roles.has('finance') || roles.has('super_admin');
  const supportDecision = async (row: any, approved: boolean) => {
    actionModal(
      approved ? '售后处理判断' : '驳回售后申请',
      approved
        ? [
            { key: 'responsibility', label: '责任方（kamei 或 store）', required: true },
            {
              key: 'approvedOutcome',
              label: '处理结果（refund_only / return_refund / reshipment）',
              required: true,
              initialValue: row.requestedOutcome,
            },
            { key: 'returnShippingApprovedCents', label: '核定退货运费（分）', initialValue: '0' },
            { key: 'reason', label: '处理说明', required: true, multiline: true },
          ]
        : [{ key: 'reason', label: '驳回原因', required: true, multiline: true }],
      async (values) => {
        if (approved && !['kamei', 'store'].includes(values.responsibility))
          throw new Error('责任方只能是 kamei 或 store');
        if (
          approved &&
          !['refund_only', 'return_refund', 'reshipment'].includes(values.approvedOutcome)
        )
          throw new Error('处理结果不正确');
        await api(`/admin/after-sales/${row.id}/support-decision`, {
          method: 'POST',
          json: {
            decision: approved ? 'support_approved' : 'support_rejected',
            reason: values.reason,
            ...(approved
              ? {
                  responsibility: values.responsibility,
                  approvedOutcome: values.approvedOutcome,
                  returnShippingApprovedCents: Number(values.returnShippingApprovedCents || 0),
                }
              : {}),
          },
        });
        message.success(approved ? '已提交售后判断' : '已驳回售后申请');
        await resource.load();
      },
    );
  };
  const columns: TableColumnsType<any> = [
    { title: '售后单号', dataIndex: 'caseNumber' },
    { title: '关联订单', render: (_, row) => row.orderNumber || row.orderId },
    { title: '数量', dataIndex: 'quantity' },
    { title: '原因', dataIndex: 'reasonCode' },
    { title: '责任', dataIndex: 'responsibility' },
    { title: '状态', dataIndex: 'status', render: statusTag },
    {
      title: '操作',
      render: (_, row) => (
        <Space wrap>
          <InternalNotesButton resourceType="after_sales" resourceId={row.id} />
          {canSupport && row.status === 'submitted' && (
            <>
              <Button type="primary" size="small" onClick={() => void supportDecision(row, true)}>
                售后通过
              </Button>
              <Button danger size="small" onClick={() => void supportDecision(row, false)}>
                售后驳回
              </Button>
            </>
          )}
          {canSupport && row.status === 'return_in_transit' && (
            <Button
              size="small"
              onClick={async () => {
                await api(`/admin/after-sales/${row.id}/return-received`, { method: 'POST' });
                message.success('退货已确认入库');
                await resource.load();
              }}
            >
              确认入库
            </Button>
          )}
          {canInspect && row.status === 'return_received' && (
            <Button
              size="small"
              onClick={() => {
                actionModal(
                  '退货逐项质检',
                  [
                    {
                      key: 'sellableQuantity',
                      label: '可再次销售数量',
                      required: true,
                      initialValue: '0',
                    },
                    {
                      key: 'damagedQuantity',
                      label: '损坏数量',
                      required: true,
                      initialValue: '0',
                    },
                    {
                      key: 'pendingInspectionQuantity',
                      label: '待检数量',
                      required: true,
                      initialValue: '0',
                    },
                  ],
                  async (values) => {
                    await api(`/admin/after-sales/${row.id}/inspection`, {
                      method: 'POST',
                      json: {
                        sellableQuantity: Number(values.sellableQuantity),
                        damagedQuantity: Number(values.damagedQuantity),
                        pendingInspectionQuantity: Number(values.pendingInspectionQuantity),
                      },
                    });
                    message.success('质检结果已记录');
                    await resource.load();
                  },
                );
              }}
            >
              退货质检
            </Button>
          )}
          {canFinance &&
            ((row.status === 'support_approved' && row.approvedOutcome === 'refund_only') ||
              (row.status === 'return_received' && row.returnPendingInspectionQuantity === 0)) && (
              <>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    actionModal(
                      '财务批准退款',
                      [{ key: 'reason', label: '复核依据', required: true, multiline: true }],
                      async ({ reason }) => {
                        await api(`/admin/after-sales/${row.id}/finance-decision`, {
                          method: 'POST',
                          json: { decision: 'finance_approved', reason },
                        });
                        message.success('财务已复核，退款进入处理队列');
                        await resource.load();
                      },
                    );
                  }}
                >
                  财务批准退款
                </Button>
                <Button
                  danger
                  size="small"
                  onClick={() => {
                    actionModal(
                      '财务拒绝退款',
                      [{ key: 'reason', label: '拒绝原因', required: true, multiline: true }],
                      async ({ reason }) => {
                        await api(`/admin/after-sales/${row.id}/finance-decision`, {
                          method: 'POST',
                          json: { decision: 'closed', reason },
                        });
                        await resource.load();
                      },
                    );
                  }}
                >
                  财务拒绝
                </Button>
              </>
            )}
          {canOperate &&
            row.status === 'support_approved' &&
            row.approvedOutcome === 'reshipment' && (
              <Button
                size="small"
                onClick={() => {
                  actionModal(
                    '登记补发',
                    [
                      { key: 'carrierName', label: '物流公司', required: true },
                      { key: 'trackingNumber', label: '物流单号', required: true },
                    ],
                    async (values) => {
                      await api(`/admin/after-sales/${row.id}/replacement/ship`, {
                        method: 'POST',
                        json: values,
                      });
                      message.success('补发单已出库，不产生销售额与提成');
                      await resource.load();
                    },
                  );
                }}
              >
                登记补发
              </Button>
            )}
        </Space>
      ),
    },
  ];
  return (
    <Table rowKey="id" columns={columns} dataSource={resource.rows} loading={resource.loading} />
  );
}

function ChatQueue() {
  const resource = useResource('/admin/chat/queue');
  const supportAgents = useResource('/admin/staff-options?role=support');
  const columns: TableColumnsType<any> = [
    { title: '门店', dataIndex: 'storeName' },
    {
      title: '负责售后',
      dataIndex: 'assignedSupportUserId',
      render: (value) =>
        value
          ? supportAgents.rows.find((person) => person.id === value)?.displayName || '已停用售后'
          : '共享队列',
    },
    { title: '更新时间', dataIndex: 'updatedAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) => (
        <Space>
          {!row.assignedSupportUserId && (
            <Button
              type="primary"
              size="small"
              onClick={async () => {
                await api(`/admin/chat/conversations/${row.id}/claim`, { method: 'POST' });
                await resource.load();
              }}
            >
              认领
            </Button>
          )}
          {row.assignedSupportUserId && (
            <Button
              size="small"
              onClick={() => {
                actionModal(
                  '转派售后会话',
                  [
                    {
                      key: 'supportUserId',
                      label: '目标售后',
                      required: true,
                      options: supportAgents.rows.map((person) => ({
                        label: person.displayName,
                        value: person.id,
                      })),
                    },
                  ],
                  async ({ supportUserId }) => {
                    await api(`/admin/chat/conversations/${row.id}/transfer`, {
                      method: 'POST',
                      json: { supportUserId },
                    });
                    message.success('会话已转派');
                    await resource.load();
                  },
                );
              }}
            >
              转派
            </Button>
          )}
        </Space>
      ),
    },
  ];
  return (
    <Table rowKey="id" columns={columns} dataSource={resource.rows} loading={resource.loading} />
  );
}

function SimpleResource({ path, columns }: { path: string; columns: TableColumnsType<any> }) {
  const resource = useResource(path);
  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={resource.rows}
      loading={resource.loading}
      scroll={{ x: 900 }}
    />
  );
}

function Employees() {
  const resource = useResource('/admin/users');
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const create = async () => {
    await api('/admin/users', { method: 'POST', json: await form.validateFields() });
    message.success('员工账号已创建');
    setOpen(false);
    form.resetFields();
    await resource.load();
  };
  const createWechatInvitation = () => {
    actionModal(
      '创建微信员工一次性邀请',
      [
        { key: 'phone', label: '员工手机号', required: true },
        {
          key: 'role',
          label: '固定角色',
          required: true,
          options: ['salesperson', 'support', 'operations', 'finance'].map((value) => ({
            label: value,
            value,
          })),
        },
        { key: 'expiresInDays', label: '有效天数', required: true, initialValue: '7' },
      ],
      async (values) => {
        const invitation = await api<any>('/admin/staff-invitations', {
          method: 'POST',
          json: {
            phone: values.phone,
            role: values.role,
            expiresInDays: Number(values.expiresInDays),
          },
        });
        Modal.info({
          title: '一次性邀请码已生成',
          width: 620,
          content: (
            <Space direction="vertical">
              <Typography.Paragraph copyable>{invitation.token}</Typography.Paragraph>
              <Typography.Text type="secondary">
                请通过可信渠道单独发送给员工；领取后立即失效，后台不会再次显示明文。
              </Typography.Text>
            </Space>
          ),
        });
      },
    );
  };
  const columns: TableColumnsType<any> = [
    { title: '姓名', dataIndex: 'displayName' },
    { title: '账号', dataIndex: 'username' },
    {
      title: '角色',
      dataIndex: 'roles',
      render: (roles: string[]) => roles?.map((r) => <Tag key={r}>{r}</Tag>),
    },
    {
      title: '操作',
      render: (_, row) => (
        <Button
          size="small"
          danger={row.isActive}
          onClick={async () => {
            await api(`/admin/users/${row.id}`, {
              method: 'PATCH',
              json: { isActive: !row.isActive },
            });
            await resource.load();
          }}
        >
          {row.isActive ? '停用' : '启用'}
        </Button>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '停用'}</Tag>,
    },
  ];
  return (
    <>
      <Button type="primary" onClick={() => setOpen(true)}>
        新增员工
      </Button>
      <Button onClick={createWechatInvitation}>邀请绑定微信身份</Button>
      <Table
        className="data-table"
        rowKey="id"
        columns={columns}
        dataSource={resource.rows}
        loading={resource.loading}
      />
      <Modal
        title="新增内部账号"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void create()}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="displayName" label="姓名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="username" label="登录账号" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码（至少 12 位）"
            rules={[{ required: true, min: 12 }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="roles" label="角色" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              options={['salesperson', 'support', 'operations', 'finance'].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function CommissionStatements() {
  const resource = useResource('/admin/commission-statements');
  const salespeople = useResource('/admin/staff-options?role=salesperson');
  const generate = async () => {
    const defaultMonth = new Date().toISOString().slice(0, 7);
    actionModal(
      '生成月度提成单',
      [{ key: 'month', label: '月份（YYYY-MM）', required: true, initialValue: defaultMonth }],
      async ({ month }) => {
        if (!/^\d{4}-\d{2}$/.test(month)) throw new Error('月份格式应为 YYYY-MM');
        await api('/admin/commission-statements/generate', {
          method: 'POST',
          json: { month },
        });
        message.success('提成单已生成');
        await resource.load();
      },
    );
  };
  const paid = async (id: string) => {
    await api(`/admin/commission-statements/${id}/paid`, { method: 'POST' });
    message.success('已标记线下发放');
    await resource.load();
  };
  const columns: TableColumnsType<any> = [
    { title: '提成单号', dataIndex: 'statementNumber' },
    { title: '月份', dataIndex: 'month' },
    {
      title: '业务员',
      dataIndex: 'salespersonUserId',
      render: (value) =>
        salespeople.rows.find((person) => person.id === value)?.displayName || '已停用业务员',
    },
    { title: '确认提成', dataIndex: 'confirmedAmountCents', render: money },
    { title: '状态', dataIndex: 'status', render: statusTag },
    { title: '发放时间', dataIndex: 'paidAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) =>
        row.status === 'draft' && (
          <Button type="primary" onClick={() => void paid(row.id)}>
            标记已线下发放
          </Button>
        ),
    },
  ];
  return (
    <>
      <Button type="primary" onClick={() => void generate()}>
        生成月度提成单
      </Button>
      <Table
        className="data-table"
        rowKey="id"
        columns={columns}
        dataSource={resource.rows}
        loading={resource.loading}
      />
    </>
  );
}

function Integrations() {
  const queue = useResource('/admin/integrations/outbox');
  const [status, setStatus] = useState<any | null>(null);
  useEffect(() => {
    api('/admin/integrations/status')
      .then(setStatus)
      .catch((error) => message.error(error instanceof Error ? error.message : '集成状态加载失败'));
  }, []);
  const columns: TableColumnsType<any> = [
    { title: '事件类型', dataIndex: 'type' },
    {
      title: '业务对象',
      render: (_, row) => `${row.aggregateType} · ${row.aggregateId}`,
    },
    { title: '尝试次数', dataIndex: 'attempts' },
    {
      title: '最近错误',
      dataIndex: 'lastError',
      ellipsis: true,
      render: (value) => value || '等待处理',
    },
    { title: '创建时间', dataIndex: 'createdAt', render: dateTime },
    {
      title: '操作',
      render: (_, row) => (
        <Button
          danger={row.attempts >= 10}
          onClick={async () => {
            await api(`/admin/integrations/outbox/${row.id}/retry`, { method: 'POST' });
            message.success('事件已重新入队');
            await queue.load();
          }}
        >
          重试
        </Button>
      ),
    },
  ];
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="首轮试运营使用人工履约与人工物流；取得吉客云和物流服务账号后替换适配器，不改变订单领域模型。"
      />
      <Row gutter={[16, 16]}>
        {['erp', 'logistics'].map((key) => (
          <Col xs={24} md={12} key={key}>
            <Card title={key === 'erp' ? '吉客云 ERP' : '物流轨迹'}>
              <p>适配器：{status?.[key]?.adapter ?? '读取中'}</p>
              {statusTag(status?.[key]?.configured ? 'configured' : 'manual')}
            </Card>
          </Col>
        ))}
      </Row>
      <Card title="Outbox 待处理与异常事件">
        <Table rowKey="id" columns={columns} dataSource={queue.rows} loading={queue.loading} />
      </Card>
    </Space>
  );
}

function Agreements() {
  const resource = useResource('/admin/agreements');
  const create = () => {
    actionModal(
      '新建不可变协议版本',
      [
        {
          key: 'type',
          label: '协议类型',
          required: true,
          options: ['user', 'privacy', 'supply', 'presale', 'shipping', 'after_sales'].map(
            (value) => ({ label: value, value }),
          ),
        },
        { key: 'version', label: '版本号', required: true, placeholder: '例如 2026-08-01' },
        { key: 'title', label: '标题', required: true },
        {
          key: 'effectiveAt',
          label: '生效时间（ISO 8601）',
          required: true,
          initialValue: new Date().toISOString(),
        },
        { key: 'content', label: '协议正文', required: true, multiline: true },
        {
          key: 'isActive',
          label: '创建后启用',
          required: true,
          initialValue: 'false',
          options: [
            { label: '否，先保存审阅', value: 'false' },
            { label: '是，立即作为当前版本', value: 'true' },
          ],
        },
      ],
      async (values) => {
        await api('/admin/agreements', {
          method: 'POST',
          json: { ...values, isActive: values.isActive === 'true' },
        });
        message.success('协议版本已创建');
        await resource.load();
      },
    );
  };
  const columns: TableColumnsType<any> = [
    { title: '类型', dataIndex: 'type' },
    { title: '版本', dataIndex: 'version' },
    { title: '标题', dataIndex: 'title' },
    { title: '生效时间', dataIndex: 'effectiveAt', render: dateTime },
    {
      title: '状态',
      dataIndex: 'isActive',
      render: (value) => (
        <Tag color={value ? 'green' : 'default'}>{value ? '当前' : '历史/草稿'}</Tag>
      ),
    },
    {
      title: '操作',
      render: (_, row) =>
        !row.isActive && (
          <Button
            onClick={async () => {
              await api(`/admin/agreements/${row.id}/activate`, { method: 'POST' });
              message.success('当前协议版本已切换');
              await resource.load();
            }}
          >
            启用
          </Button>
        ),
    },
  ];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="warning"
        showIcon
        message="正式启用前必须完成法律/合规审阅；启用新用户或隐私版本后，未确认用户会被强制进入确认页。"
      />
      <Button type="primary" onClick={create}>
        新建协议版本
      </Button>
      <Table rowKey="id" columns={columns} dataSource={resource.rows} loading={resource.loading} />
    </Space>
  );
}

function statusTag(value: unknown) {
  const text = String(value ?? '—');
  const good = ['verified', 'paid', 'delivered', 'succeeded', 'completed'].includes(text);
  const bad = ['rejected', 'suspended', 'failed', 'closed'].includes(text);
  return <Tag color={good ? 'green' : bad ? 'red' : 'gold'}>{text}</Tag>;
}

function ViewContent({ view, roles }: { view: View; roles: Set<string> }) {
  const standardColumns: TableColumnsType<any> = [
    {
      title: '业务编号',
      render: (_, row) => row.refundNumber || row.statementNumber || row.orderNumber || '—',
    },
    { title: '关联订单', dataIndex: 'orderNumber' },
    { title: '状态', dataIndex: 'status', render: statusTag },
    { title: '金额', dataIndex: 'amountCents', render: money },
    { title: '时间', dataIndex: 'createdAt', render: dateTime },
  ];
  switch (view) {
    case 'overview':
      return <Overview />;
    case 'applications':
      return <Applications />;
    case 'stores':
      return <Stores />;
    case 'catalog':
      return <Catalog />;
    case 'inventory':
      return <Inventory />;
    case 'orders':
      return <Orders />;
    case 'shipping':
      return <Orders shipping />;
    case 'afterSales':
      return <AfterSales roles={roles} />;
    case 'chat':
      return <ChatQueue />;
    case 'refunds':
      return <SimpleResource path="/admin/refunds" columns={standardColumns} />;
    case 'commissions':
      return <CommissionStatements />;
    case 'users':
      return <Employees />;
    case 'agreements':
      return <Agreements />;
    case 'integrations':
      return <Integrations />;
    case 'audit':
      return (
        <SimpleResource
          path="/admin/audit-logs"
          columns={[
            { title: '操作', dataIndex: 'action' },
            { title: '资源', render: (_, r) => `${r.resourceType} · ${r.resourceId}` },
            { title: '操作人', dataIndex: 'actorUserId', ellipsis: true },
            { title: '时间', dataIndex: 'createdAt', render: dateTime },
          ]}
        />
      );
  }
}

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [view, setView] = useState<View>('overview');
  useEffect(() => {
    api<Session>('/auth/me')
      .then(setSession)
      .catch((error) =>
        setSession(error instanceof ApiError && error.status === 401 ? null : null),
      );
  }, []);
  const roleLabel = useMemo(
    () =>
      session?.contexts
        .map((item) => item.role)
        .filter((role) => !role.startsWith('store_'))
        .join(' · '),
    [session],
  );
  const allowedViews = useMemo(() => {
    const roles = new Set(session?.contexts.map((item) => item.role) ?? []);
    return new Set(
      (Object.keys(viewRoles) as View[]).filter((candidate) =>
        viewRoles[candidate].some((role) => roles.has(role)),
      ),
    );
  }, [session]);
  const visibleMenuItems = useMemo(
    () =>
      (menuItems ?? [])
        .map((item: any) =>
          item.children
            ? {
                ...item,
                children: item.children.filter((child: any) => allowedViews.has(child.key)),
              }
            : item,
        )
        .filter((item: any) =>
          item.children ? item.children.length > 0 : allowedViews.has(item.key),
        ),
    [allowedViews],
  );
  useEffect(() => {
    if (session && !allowedViews.has(view)) {
      setView(allowedViews.has('overview') ? 'overview' : ([...allowedViews][0] ?? 'overview'));
    }
  }, [allowedViews, session, view]);
  if (session === undefined)
    return (
      <div className="center-spin">
        <Spin size="large" />
      </div>
    );
  if (!session) return <Login onLogin={setSession} />;
  if (session.passwordMustChange) return <ChangePassword onChanged={setSession} />;
  return (
    <Layout className="app-layout">
      <Layout.Sider width={248} breakpoint="lg" collapsedWidth="0">
        <div className="sidebar-brand">
          <span>KM</span>
          <div>
            <strong>卡美供应链</strong>
            <small>管理中心</small>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[view]}
          defaultOpenKeys={['customers', 'system']}
          items={visibleMenuItems}
          onClick={({ key }) => setView(key as View)}
        />
      </Layout.Sider>
      <Layout>
        <Layout.Header className="topbar">
          <div>
            <Typography.Title level={3}>{labels[view]}</Typography.Title>
            <span>所有关键操作均写入审计记录</span>
          </div>
          <Space>
            <div className="operator">
              <strong>{session.displayName}</strong>
              <small>{roleLabel}</small>
            </div>
            <Button
              icon={<LogoutOutlined />}
              onClick={async () => {
                await api('/auth/admin/logout', { method: 'POST' });
                setSession(null);
              }}
            >
              退出
            </Button>
          </Space>
        </Layout.Header>
        <Layout.Content className="content">
          <ViewContent
            view={view}
            roles={new Set(session.contexts.map((context) => context.role))}
          />
        </Layout.Content>
      </Layout>
    </Layout>
  );
}
